import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Select from "react-select";

import { create } from '@api/v4/resources/conversion_presets';
import Checkbox from '@components/common/checkbox/main';

const PresetForm = ({ resourceType, resourceKey, onAddPreset }) => {
  const [submitting, setSubmitting] = useState(false);
  const [name, setName] = useState('');
  const [aspectWidth, setAspectWidth] = useState(1);
  const [aspectHeight, setAspectHeight] = useState(1);
  const [width, setWidth] = useState(200);
  const [height, setHeight] = useState(200);
  const [format, setFormat] = useState(null);
  const [aspectOnly, setAspectOnly] = useState(false);
  const [error, setError] = useState(null);

  const calculatedAspectRatio = parseInt(aspectWidth, 10) / parseInt(aspectHeight, 10);

  const submit = (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);

    create({
      resourceType,
      resourceKey,
      name,
      aspectWidth: aspectOnly ? aspectWidth : undefined,
      aspectHeight: aspectOnly ? aspectHeight : undefined,
      width,
      height,
      format,
    }).then(() => {
      onAddPreset();
      setName('');
      setAspectWidth(1);
      setAspectHeight(1);
      setWidth(200);
      setHeight(200);
      setFormat(null);
      setAspectOnly(false);
    }).catch(({ errors }) => {
      setError(errors);
    }).finally(() => {
      setSubmitting(false);
    });
  };

  const updateWidth = (newWidth) => {
    if (aspectOnly) {
      const newHeight = Math.round(parseInt(newWidth, 10) / calculatedAspectRatio);
      setHeight(Number.isNaN(newHeight) || newHeight <= 0 ? '' : newHeight);
    }

    setWidth(newWidth);
  };

  const updateHeight = (newHeight) => {
    if (aspectOnly) {
      const newWidth = Math.round(parseInt(newHeight, 10) * calculatedAspectRatio);
      setWidth(Number.isNaN(newWidth) || newWidth <= 0 ? '' : newWidth);
    }

    setHeight(newHeight);
  };

  useEffect(() => {
    // Force the height value to update to match the aspect ratio once they toggle that on
    if (aspectOnly) {
      updateWidth(width);
    }
  }, [aspectWidth, aspectHeight, aspectOnly]);

  return (
    <form
      className="cropping--preset-form"
      onSubmit={submit}
    >
      <div>
        <label
          className="bf-label"
          htmlFor="preset-name"
        >
          Preset Name
          <input
            name="preset-name"
            id="preset-name"
            className="bf-input"
            type="text"
            value={name}
            onChange={({ target }) => setName(target.value)}
            disabled={submitting}
            required
          />
        </label>

        <label
          className="bf-label"
          htmlFor="preset-format"
        >
          Download Format
          <Select
            name="preset-format"
            options={[
              { value: null, label: 'Allow User To Select' },
              { value: 'JPG', label: 'JPG' },
              { value: 'PNG', label: 'PNG' },
            ]}
            value={format}
            searchable={false}
            placeholder="Allow User To Select"
            onChange={({ value }) => setFormat(value)}
            isDisabled={submitting}
          />
        </label>
      </div>
      <div>
        <label
          className="bf-label"
          htmlFor="preset-width"
        >
          Width (px)
          <input
            name="preset-width"
            id="preset-width"
            className="bf-input"
            type="number"
            value={width}
            onChange={({ target }) => updateWidth(target.value)}
            disabled={submitting}
            pattern="\d+"
            required={!aspectOnly}
          />
        </label>
        <label
          className="bf-label"
          htmlFor="preset-height"
        >
          Height (px)
          <input
            name="preset-height"
            id="preset-height"
            className="bf-input"
            type="number"
            value={height}
            onChange={({ target }) => updateHeight(target.value)}
            disabled={submitting}
            pattern="\d+"
            required={!aspectOnly}
          />
        </label>
      </div>

      <div className="preset--aspect-ratio-container">
        <div>
          <label
            className="bf-label"
            htmlFor="preset-aspect-ratio"
          >
            Aspect Ratio
          </label>
          <div className="inputs-container">
            <input
              name="preset-aspect-width"
              id="preset-aspect-width"
              className="bf-input"
              type="number"
              value={aspectWidth}
              onChange={({ target }) => setAspectWidth(target.value)}
              disabled={submitting || !aspectOnly}
              pattern="\d+"
              required={aspectOnly}
            />
            <span className="preset--aspect-ratio-divider">:</span>
            <input
              name="preset-aspect-height"
              id="preset-aspect-height"
              className="bf-input"
              type="number"
              value={aspectHeight}
              onChange={({ target }) => setAspectHeight(target.value)}
              disabled={submitting || !aspectOnly}
              pattern="\d+"
              required={aspectOnly}
            />
          </div>
        </div>
        <Checkbox
          checked={aspectOnly}
          size="lg"
          toggle={() => setAspectOnly(!aspectOnly)}
          label="Lock aspect ratio"
        />
      </div>

      <div className={`cropping--button-container ${error && 'has-error'}`}>
        {error && (
          <p className="error-message">{error}</p>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="button primary sm"
        >
          Create Preset
        </button>
      </div>
    </form>
  );
};

PresetForm.propTypes = {
  resourceKey: PropTypes.string.isRequired,
  onAddPreset: PropTypes.func,
  resourceType: PropTypes.string,
};

PresetForm.defaultProps = {
  onAddPreset: () => {},
  resourceType: 'brandfolder',
};

export default PresetForm;
